import { Component } from '@theme/component';

// Behavior constants. The open delay is editor-configurable on the
// container (data-open-delay, default 0 = instant trigger); the rest
// stays constant on purpose.
const OPEN_DELAY = 0; // ms fallback until a hovered mega item opens its panel
const CLOSE_DELAY = 300; // ms until leaving the item/panel closes it
const RAIL_SWITCH_DELAY = 40; // ms until a hovered rail item switches the flyout
const TRIANGLE_IDLE_TIMEOUT = 300; // ms the safe triangle stays valid without pointer movement

/**
 * Mega navigation container (`blocks/jgd-mega.liquid`).
 *
 * - Opens/closes the axis panels (trigger mode `hover` | `click` via the
 *   container's `data-trigger` attribute; hover = delayed hover opens,
 *   click navigates; click = click toggles).
 * - Reuses the stock header submenu mechanics: sets `--submenu-height` and
 *   `--full-open-header-height` on `header-component` so Horizon's
 *   `.header__underlay-open` paints background + shadow and the panel's
 *   clip-path reveal works (same variables as stock `header-menu.js`).
 * - Rail hover switches flyouts with a safe-triangle heuristic; keyboard
 *   (focus/disclosure buttons) bypasses the triangle entirely.
 * - Adopts the items of a sibling `jgd-nav-extra` container into the
 *   nav list (right-aligned). No overflow/"More" handling — the list
 *   simply wraps when space runs out.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list
 * @property {HTMLElement} scrim
 * @property {HTMLElement[]} [megaItem]
 * @property {HTMLElement[]} [megaToggle]
 * @property {HTMLElement[]} [megaPanel]
 *
 * @extends {Component<Refs>}
 */
class JgdMegaNav extends Component {
  requiredRefs = ['list'];

  /** @type {HTMLElement | null} */
  #openItem = null;

  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #railTimer;

  /**
   * Short pointer history (~150ms window). Direction decisions use the
   * aggregate over the window — single-sample deltas jitter too much
   * and would flip the safe-triangle protection mid-travel.
   * @type {Array<{ x: number, y: number, t: number }>}
   */
  #pointerTrail = [];

  /**
   * Pending flyout/column switch deferred by the safe triangle —
   * re-evaluated on every pointer move so the switch fires as soon as
   * the pointer is no longer heading towards the open target.
   * @type {{ target: Element, run: () => void } | null}
   */
  #pendingSwitch = null;

  /** @type {MutationObserver | null} */
  #hydrationObserver = null;

  /** @type {MutationObserver | null} */
  #menuStyleObserver = null;

  /** @type {ResizeObserver} */
  #panelResizeObserver = new ResizeObserver(() => {
    if (this.#openItem) this.#applyHeights(this.#openItem);
  });

  get trigger() {
    return this.dataset.trigger === 'click' ? 'click' : 'hover';
  }

  get openDelay() {
    const value = parseInt(this.dataset.openDelay ?? '', 10);
    return Number.isFinite(value) && value >= 0 ? value : OPEN_DELAY;
  }

  /** Panel-Darstellung: 'grid' (Ebene 2/3 als Raster) | 'columns' (Kaskade). */
  get panelLayout() {
    return this.dataset.panelLayout === 'columns' ? 'columns' : 'grid';
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#adoptExtras();

    const megaItems = this.refs.megaItem ?? [];

    for (const item of megaItems) {
      item.addEventListener('pointerenter', this.#onItemPointerEnter);
      item.addEventListener('pointerleave', this.#onItemPointerLeave);
      item.addEventListener('focusout', this.#onItemFocusOut);

      const link = item.querySelector('a.jgd-mega__link');
      link?.addEventListener('click', this.#onLinkClick);

      const toggle = item.querySelector('[ref="megaToggle[]"]');
      toggle?.addEventListener('click', this.#onToggleClick);

      const panel = this.#panelOf(item);
      if (panel) {
        panel.addEventListener('pointermove', this.#onPanelPointerMove);
        panel.addEventListener('pointerenter', this.#cancelClose);
        panel.addEventListener('pointerleave', this.#onItemPointerLeave);
        panel.addEventListener('click', this.#onPanelClick);
        // Delegiert (Ebene 2/3 kommen per Hydration nach — direkte
        // Listener würden die gemorphten Knoten verfehlen).
        panel.addEventListener('pointerover', this.#onPanelPointerOver);
      }

      this.#bindRail(item);
    }

    this.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);

    this.#observeMenuStyle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    this.#panelResizeObserver.disconnect();
    this.#hydrationObserver?.disconnect();
    this.#menuStyleObserver?.disconnect();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#railTimer);
  }

  /* ===== Extras adoption ===== */

  #adoptExtras() {
    const header = this.headerComponent;
    if (!header) return;

    for (const extra of header.querySelectorAll('jgd-nav-extra-nav:not([data-jgd-adopted])')) {
      const items = extra.querySelectorAll('.jgd-nav-links__item');
      let first = !this.refs.list.querySelector('[data-jgd-extra-first]');

      // Opt-in typography of the extra container travels with the items
      // (inline vars); without it they inherit the mega nav's font vars.
      const fontStyle = extra.getAttribute('style');

      for (const li of items) {
        if (fontStyle) li.setAttribute('style', fontStyle);
        li.setAttribute('data-jgd-extra', '');
        if (first) {
          li.setAttribute('data-jgd-extra-first', '');
          first = false;
        }
        this.refs.list.appendChild(li);
      }

      extra.setAttribute('data-jgd-adopted', '');
    }
  }

  /* ===== Panel open/close ===== */

  /**
   * @param {PointerEvent} event
   */
  #onItemPointerEnter = (event) => {
    this.#cancelClose();
    if (this.trigger !== 'hover') return;
    if (event.pointerType === 'touch') return;

    const item = /** @type {HTMLElement} */ (event.currentTarget);
    if (item === this.#openItem) return;

    clearTimeout(this.#openTimer);
    // Switch instantly between panels, delay only the initial open.
    const delay = this.#openItem ? 0 : this.openDelay;
    this.#openTimer = setTimeout(() => this.#open(item), delay);
  };

  #onItemPointerLeave = () => {
    clearTimeout(this.#openTimer);
    if (this.trigger !== 'hover') return;
    this.#scheduleClose();
  };

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
  };

  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.closeAll(), CLOSE_DELAY);
  }

  /**
   * Click on the mega link: in `click` mode (or without a configured
   * target) the click toggles the panel instead of navigating.
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    if (this.trigger !== 'click') return;

    event.preventDefault();
    const item = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.currentTarget).closest('.jgd-mega__item')
    );
    if (!item) return;
    this.#toggle(item, /** @type {HTMLElement} */ (event.currentTarget));
  };

  /**
   * @param {MouseEvent} event
   */
  #onToggleClick = (event) => {
    const toggle = /** @type {HTMLElement} */ (event.currentTarget);
    const item = /** @type {HTMLElement | null} */ (toggle.closest('.jgd-mega__item'));
    if (!item) return;
    this.#toggle(item, toggle);
  };

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} trigger
   */
  #toggle(item, trigger) {
    if (this.#openItem === item) {
      this.closeAll();
    } else {
      this.#lastTrigger = trigger;
      this.#open(item);
    }
  }

  /**
   * @param {HTMLElement} item
   */
  #open(item) {
    if (this.#openItem === item) return;
    if (this.#openItem) this.#close(this.#openItem, { keepScrim: true });

    this.#openItem = item;
    item.setAttribute('data-jgd-open', '');
    this.setAttribute('data-jgd-open', '');
    this.#setExpanded(item, true);

    // No default flyout on open: the panel starts with the rail only,
    // level 2/3 appear on rail hover/focus. Also clears a flyout left
    // active from a previous open.
    this.#activateFlyout(item, null);

    this.#watchHydration(item);
    this.#applyHeights(item);

    const panel = this.#panelOf(item);
    if (panel) this.#panelResizeObserver.observe(panel);
  }

  /**
   * @param {HTMLElement} item
   * @param {{ keepScrim?: boolean }} [options]
   */
  #close(item, options = {}) {
    item.removeAttribute('data-jgd-open');
    this.#setExpanded(item, false);

    const panel = this.#panelOf(item);
    if (panel) this.#panelResizeObserver.unobserve(panel);

    if (!options.keepScrim) {
      this.removeAttribute('data-jgd-open');
      this.headerComponent?.style.setProperty('--submenu-height', '0px');
      this.headerComponent?.style.setProperty('--full-open-header-height', '0px');
    }

    if (this.#openItem === item) this.#openItem = null;
    this.#hydrationObserver?.disconnect();
    this.#hydrationObserver = null;
  }

  closeAll() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    if (this.#openItem) this.#close(this.#openItem);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    for (const toggle of item.querySelectorAll(':scope > [aria-controls], :scope > .jgd-mega__link[aria-controls]')) {
      toggle.setAttribute('aria-expanded', `${expanded}`);
    }
  }

  /**
   * Mirror of stock header-menu.js height bookkeeping so Horizon's
   * underlay paints the panel background and the clip-path reveal works.
   * @param {HTMLElement} item
   */
  #applyHeights(item) {
    const header = this.headerComponent;
    const panel = this.#panelOf(item);
    if (!header || !panel) return;

    const inner = /** @type {HTMLElement | null} */ (panel.querySelector('.jgd-mega__panel-inner'));
    const submenuHeight = inner?.offsetHeight ?? 0;

    const isOverlap = header.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight = isOverlap
      ? /** @type {HTMLElement | null} */ (header.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : header.offsetHeight;

    header.style.setProperty('--submenu-height', `${submenuHeight}px`);
    header.style.setProperty(
      '--full-open-header-height',
      `${submenuHeight === 0 ? 0 : submenuHeight + headerVisibleHeight}px`
    );
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #panelOf(item) {
    return item.querySelector(':scope > .jgd-mega__panel');
  }

  /* ===== Rail / flyout ===== */

  /**
   * @param {HTMLElement} item
   */
  #bindRail(item) {
    for (const railLink of item.querySelectorAll('.jgd-mega__rail-link')) {
      railLink.addEventListener('pointerenter', this.#onRailPointerEnter);
    }

    for (const railToggle of item.querySelectorAll('.jgd-mega__rail-toggle')) {
      railToggle.addEventListener('click', this.#onRailToggleClick);
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onRailPointerEnter = (event) => {
    if (event.pointerType === 'touch') return;
    const link = /** @type {HTMLElement} */ (event.currentTarget);

    clearTimeout(this.#railTimer);
    this.#pendingSwitch = null;

    const run = () => {
      this.#pendingSwitch = null;
      if (!link.matches(':hover')) return;
      const item = /** @type {HTMLElement | null} */ (link.closest('.jgd-mega__item'));
      if (!item) return;
      this.#activateFlyout(item, link.getAttribute('data-jgd-flyout-for'));
    };

    const openFlyout = this.#openItem?.querySelector('.jgd-mega__flyout[data-jgd-active]');
    if (openFlyout && this.#inSafeTriangleTo(openFlyout)) {
      // Pointer is heading towards the open flyout — defer; the next
      // pointer move that no longer aims at it fires the switch at once.
      this.#pendingSwitch = { target: openFlyout, run };
      this.#railTimer = setTimeout(run, TRIANGLE_IDLE_TIMEOUT);
    } else {
      this.#railTimer = setTimeout(run, RAIL_SWITCH_DELAY);
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onRailToggleClick = (event) => {
    const toggle = /** @type {HTMLElement} */ (event.currentTarget);
    const item = /** @type {HTMLElement | null} */ (toggle.closest('.jgd-mega__item'));
    if (!item) return;

    const index = toggle.getAttribute('data-jgd-flyout-for');
    const isActive = index != null && index === this.#activeFlyoutIndex(item);
    this.#activateFlyout(item, isActive ? null : index);
  };

  /**
   * @param {HTMLElement} item
   * @returns {string | null}
   */
  #activeFlyoutIndex(item) {
    return item.querySelector('.jgd-mega__flyout[data-jgd-active]')?.getAttribute('data-jgd-flyout') ?? null;
  }

  /**
   * Shows the flyout for the given rail index (null = none, e.g. leaf
   * rail items). Tolerates pending hydration: the rail state is applied,
   * the flyout follows once it exists.
   * @param {HTMLElement} item
   * @param {string | null} index
   */
  #activateFlyout(item, index) {
    for (const railItem of item.querySelectorAll('.jgd-mega__rail-item')) {
      const link = railItem.querySelector('.jgd-mega__rail-link');
      const railIndex = link?.getAttribute('data-jgd-flyout-for') ?? null;
      railItem.toggleAttribute('data-jgd-active', index != null && railIndex === index);
      railItem
        .querySelector('.jgd-mega__rail-toggle')
        ?.setAttribute('aria-expanded', `${index != null && railIndex === index}`);
    }

    for (const flyout of item.querySelectorAll('.jgd-mega__flyout')) {
      flyout.toggleAttribute('data-jgd-active', flyout.getAttribute('data-jgd-flyout') === index);
    }

    // Flyout-Wechsel startet die Ebene-3-Spalte (columns-Modus) frisch.
    this.#activateL2(item, null);

    item.setAttribute('data-jgd-desired-flyout', index ?? '');
    if (this.#openItem === item) this.#applyHeights(item);
  }

  /* ===== Ebene-2-Kaskade (panel_layout: columns) ===== */

  /**
   * Aktiviert die Ebene-3-Spalte der Gruppe `index` im aktiven Flyout
   * (null = keine). Wirkt nur auf Attribute — die Darstellung macht CSS.
   * @param {HTMLElement} item
   * @param {string | null} index
   */
  #activateL2(item, index) {
    const flyout = item.querySelector('.jgd-mega__flyout[data-jgd-active]') ?? item;
    for (const group of flyout.querySelectorAll('.jgd-mega__group')) {
      const groupIndex = group.querySelector('[data-jgd-l2-for]')?.getAttribute('data-jgd-l2-for') ?? null;
      const active = index != null && groupIndex === index;
      group.toggleAttribute('data-jgd-active-l2', active);
      group.querySelector('.jgd-mega__group-toggle')?.setAttribute('aria-expanded', `${active}`);
    }
  }

  /**
   * @param {HTMLElement} item
   * @returns {string | null}
   */
  #activeL2Index(item) {
    return (
      item
        .querySelector('.jgd-mega__group[data-jgd-active-l2] [data-jgd-l2-for]')
        ?.getAttribute('data-jgd-l2-for') ?? null
    );
  }

  /**
   * Delegierter Hover auf Ebene-2-Punkte (columns-Modus): wechselt die
   * Ebene-3-Spalte mit Safe Triangle Richtung aktiver Spalte.
   * @param {PointerEvent} event
   */
  #onPanelPointerOver = (event) => {
    if (this.panelLayout !== 'columns') return;
    if (event.pointerType === 'touch') return;
    if (!(event.target instanceof Element)) return;

    const head = event.target.closest('.jgd-mega__group-head');
    if (!head) return;

    const item = /** @type {HTMLElement | null} */ (head.closest('.jgd-mega__item'));
    if (!item) return;

    const link = head.querySelector('.jgd-mega__group-link');
    const index = link?.getAttribute('data-jgd-l2-for') ?? null;
    if (index === this.#activeL2Index(item)) return;

    clearTimeout(this.#railTimer);
    this.#pendingSwitch = null;

    const run = () => {
      this.#pendingSwitch = null;
      if (head.matches(':hover')) this.#activateL2(item, index);
    };

    const activeList = item.querySelector('.jgd-mega__group[data-jgd-active-l2] > .jgd-mega__group-list');
    if (activeList && this.#inSafeTriangleTo(activeList)) {
      this.#pendingSwitch = { target: activeList, run };
      this.#railTimer = setTimeout(run, TRIANGLE_IDLE_TIMEOUT);
    } else {
      this.#railTimer = setTimeout(run, RAIL_SWITCH_DELAY);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onPanelPointerMove = (event) => {
    const now = Date.now();
    const trail = this.#pointerTrail;
    trail.push({ x: event.clientX, y: event.clientY, t: now });
    while (trail.length > 1 && (now - trail[0].t > 150 || trail.length > 12)) trail.shift();

    // A deferred switch fires once the pointer no longer heads towards
    // the open flyout/column (instead of waiting the full timeout).
    if (this.#pendingSwitch && !this.#inSafeTriangleTo(this.#pendingSwitch.target)) {
      const pending = this.#pendingSwitch;
      this.#pendingSwitch = null;
      clearTimeout(this.#railTimer);
      pending.run();
    }
  };

  /**
   * Triangle core: windowed anchor position + the target's left edge
   * corners (also used for the level-3 column in columns mode).
   * Protective only with NET motion towards the target over the trail
   * window — pure vertical rail scanning has net dx ≈ 0 and switches
   * fast; diagonal travel towards the target stays protected even with
   * per-sample jitter.
   * @param {Element} target
   * @returns {boolean}
   */
  #inSafeTriangleTo(target) {
    const trail = this.#pointerTrail;
    const point = trail[trail.length - 1];
    const anchor = trail[0];
    if (!point || !anchor || point === anchor) return false;
    if (Date.now() - point.t > TRIANGLE_IDLE_TIMEOUT) return false;

    const dx = point.x - anchor.x;
    const dy = point.y - anchor.y;
    // Net motion over the window must point towards the target
    // (LTR: it sits right of the rail/column) and be non-trivial.
    if (dx < 1 || Math.hypot(dx, dy) < 3) return false;

    const rect = target.getBoundingClientRect();
    if (rect.width === 0) return false;

    const a = { x: anchor.x, y: anchor.y };
    const b = { x: rect.left, y: rect.top };
    const c = { x: rect.left, y: rect.bottom };

    return pointInTriangle(point, a, b, c);
  }

  /**
   * Panel clicks (delegated): level-2 disclosure toggles switch the
   * level-3 column; navigation links close the panel.
   * @param {MouseEvent} event
   */
  #onPanelClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const groupToggle = event.target.closest('.jgd-mega__group-toggle');
    if (groupToggle) {
      const item = /** @type {HTMLElement | null} */ (groupToggle.closest('.jgd-mega__item'));
      const index = groupToggle.getAttribute('data-jgd-l2-for');
      if (item && index != null) {
        this.#activateL2(item, index === this.#activeL2Index(item) ? null : index);
      }
      return;
    }

    const link = event.target.closest('a[href]');
    if (link) this.closeAll();
  };

  /* ===== Hydration ===== */

  /**
   * While level 2/3 are not hydrated yet, watch the flyout container and
   * re-apply the desired flyout + heights once the content arrives.
   * @param {HTMLElement} item
   */
  #watchHydration(item) {
    const flyouts = item.querySelector('.jgd-mega__flyouts');
    if (!flyouts || !flyouts.hasAttribute('data-jgd-pending')) return;

    this.#hydrationObserver?.disconnect();
    this.#hydrationObserver = new MutationObserver(() => {
      if (flyouts.hasAttribute('data-jgd-pending')) return;
      this.#hydrationObserver?.disconnect();
      this.#hydrationObserver = null;

      const desired = item.getAttribute('data-jgd-desired-flyout');
      this.#activateFlyout(item, desired === '' ? null : desired);
      if (this.#openItem === item) this.#applyHeights(item);
    });
    this.#hydrationObserver.observe(flyouts, { childList: true, attributes: true });
  }

  /* ===== Keyboard / outside interactions ===== */

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    // Tab from an EXPANDED rail item jumps straight into its flyout
    // links (they are not DOM-adjacent — naturally Tab would first walk
    // the entire rail); Shift+Tab from the first flyout link goes back.
    if (event.key === 'Tab') {
      const item = this.#openItem;
      const target = event.target instanceof Element ? event.target : null;
      if (!item || !target) return;

      const activeIndex = this.#activeFlyoutIndex(item);
      if (activeIndex == null) return;
      const flyout = item.querySelector(`.jgd-mega__flyout[data-jgd-flyout="${activeIndex}"]`);
      if (!flyout) return;

      // Nur SICHTBARE Links zählen — im columns-Modus sind die
      // Ebene-3-Listen inaktiver Gruppen display:none.
      const visibleLinks = [...flyout.querySelectorAll('a[href]')].filter(
        (a) => a instanceof HTMLElement && a.offsetParent !== null
      );
      const firstLink = visibleLinks[0];
      const lastLink = visibleLinks[visibleLinks.length - 1];
      if (!(firstLink instanceof HTMLElement)) return;

      const activeGroup = flyout.querySelector('.jgd-mega__group[data-jgd-active-l2]');
      const listLinks = activeGroup ? [...activeGroup.querySelectorAll('.jgd-mega__group-list a[href]')] : [];

      /** @param {Element | null | undefined} el */
      const jumpTo = (el) => {
        if (el instanceof HTMLElement) {
          event.preventDefault();
          el.focus();
          return true;
        }
        return false;
      };

      if (!event.shiftKey) {
        // Rail-Punkt mit offenem Flyout → hinein in die Unterpunkte
        const railOwner = target.closest('[data-jgd-flyout-for]');
        if (railOwner && item.contains(railOwner) && railOwner.getAttribute('data-jgd-flyout-for') === activeIndex) {
          jumpTo(firstLink);
          return;
        }

        // Ebene-2-Punkt mit offener Ebene-3-Spalte → hinein (columns)
        const l2Owner = target.closest('[data-jgd-l2-for]');
        if (
          l2Owner &&
          flyout.contains(l2Owner) &&
          activeGroup &&
          l2Owner.getAttribute('data-jgd-l2-for') === this.#activeL2Index(item)
        ) {
          jumpTo(listLinks[0]);
          return;
        }

        // Ende der Ebene-3-Spalte → nächster Ebene-2-Punkt (columns)
        if (listLinks.length && target === listLinks[listLinks.length - 1]) {
          const nextGroupLink = activeGroup?.nextElementSibling?.querySelector('.jgd-mega__group-link');
          if (jumpTo(nextGroupLink)) return;
        }

        // Letzter sichtbarer Flyout-Link → nächster Rail-Punkt
        if (target === lastLink || (listLinks.length && target === listLinks[listLinks.length - 1])) {
          const ownerItem = item
            .querySelector(`.jgd-mega__rail-link[data-jgd-flyout-for="${activeIndex}"]`)
            ?.closest('.jgd-mega__rail-item');
          jumpTo(ownerItem?.nextElementSibling?.querySelector('.jgd-mega__rail-link'));
        }
        return;
      }

      // Shift+Tab: vom ersten Unterpunkt zurück zum jeweiligen Elternpunkt
      if (target === firstLink) {
        jumpTo(item.querySelector(`.jgd-mega__rail-link[data-jgd-flyout-for="${activeIndex}"]`));
        return;
      }
      if (listLinks.length && target === listLinks[0]) {
        jumpTo(activeGroup?.querySelector('.jgd-mega__group-link'));
      }
      return;
    }

    // Space on focused trigger/rail LINKS toggles (buttons do this
    // natively; Enter keeps navigating the link).
    if (event.key === ' ') {
      const target = event.target instanceof Element ? event.target : null;

      const megaLink = target?.closest('a.jgd-mega__link');
      if (megaLink instanceof HTMLElement) {
        event.preventDefault();
        const item = /** @type {HTMLElement | null} */ (megaLink.closest('.jgd-mega__item'));
        if (item) this.#toggle(item, megaLink);
        return;
      }

      const railLink = target?.closest('a.jgd-mega__rail-link[data-jgd-flyout-for]');
      if (railLink instanceof HTMLElement) {
        event.preventDefault();
        const item = /** @type {HTMLElement | null} */ (railLink.closest('.jgd-mega__item'));
        const index = railLink.getAttribute('data-jgd-flyout-for');
        if (item) {
          const isActive = index != null && index === this.#activeFlyoutIndex(item);
          this.#activateFlyout(item, isActive ? null : index);
        }
        return;
      }

      // Ebene-2-Link (columns-Modus): Space toggelt die Ebene-3-Spalte
      const groupLink = target?.closest('a.jgd-mega__group-link[data-jgd-l2-for]');
      if (groupLink instanceof HTMLElement && this.panelLayout === 'columns') {
        event.preventDefault();
        const item = /** @type {HTMLElement | null} */ (groupLink.closest('.jgd-mega__item'));
        const index = groupLink.getAttribute('data-jgd-l2-for');
        if (item) {
          this.#activateL2(item, index != null && index === this.#activeL2Index(item) ? null : index);
        }
      }
      return;
    }

    if (event.key !== 'Escape') return;

    if (!this.#openItem) return;

    const item = this.#openItem;
    this.closeAll();

    const focusTarget =
      this.#lastTrigger && item.contains(this.#lastTrigger)
        ? this.#lastTrigger
        : /** @type {HTMLElement | null} */ (item.querySelector('.jgd-mega__link'));
    focusTarget?.focus();
  };

  /**
   * @param {FocusEvent} event
   */
  #onItemFocusOut = (event) => {
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    if (item !== this.#openItem) return;
    if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
    this.closeAll();
  };

  /**
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#openItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.closeAll();
  };

  /* ===== Menu style (drawer switch) ===== */

  #observeMenuStyle() {
    const header = this.headerComponent;
    if (!header) return;

    this.#menuStyleObserver = new MutationObserver(() => {
      if (header.dataset.menuStyle === 'drawer') this.closeAll();
    });
    this.#menuStyleObserver.observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
  }
}

/**
 * @param {{ x: number, y: number }} p
 * @param {{ x: number, y: number }} a
 * @param {{ x: number, y: number }} b
 * @param {{ x: number, y: number }} c
 * @returns {boolean}
 */
function pointInTriangle(p, a, b, c) {
  const sign = (p1, p2, p3) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = sign(p, a, b);
  const d2 = sign(p, b, c);
  const d3 = sign(p, c, a);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

if (!customElements.get('jgd-mega-nav')) {
  customElements.define('jgd-mega-nav', JgdMegaNav);
}
